/* ==========================================================================
   Sprayo CRO — build a bundle. Audit BN-01, BN-02.

   Quantities are open-ended here, so unlike the product page there is no
   fixed set of combinations to price in Liquid. Money is therefore formatted
   in JS — through the shop's own money_format, so it reads identically to
   every other price on the site rather than inventing a second style.
   ========================================================================== */

if (!customElements.get("sprayo-builder")) {
  customElements.define(
    "sprayo-builder",
    class SprayoBuilder extends HTMLElement {
      connectedCallback() {
        const payload = this.querySelector("[data-sprayo-builder]");
        if (!payload) return;
        try {
          this.products = JSON.parse(payload.textContent).products || [];
        } catch (e) {
          console.error("[sprayo] builder payload is not valid JSON", e);
          return;
        }
        if (!this.products.length) return;

        this.min = parseInt(this.dataset.min, 10) || 0;
        this.moneyFormat = this.dataset.moneyFormat || "{{amount}}";
        const [one, many] = (this.dataset.labels || "bottle|bottles").split("|");
        this.saveWord = this.dataset.saveWord || "Save";
        this.words = { one, many };

        // "6:20,10:25" — thresholds ascending, so the last match wins.
        // The percent is clamped: a malformed ladder should cost nothing
        // rather than produce a negative total, which is what an earlier
        // separator bug did.
        this.tiers = (this.dataset.tiers || "")
          .split(",")
          .map((pair) => {
            const [qty, pct] = pair.split(":");
            return {
              qty: parseInt(qty, 10),
              pct: Math.min(100, Math.max(0, parseInt(pct, 10) || 0)),
            };
          })
          .filter((t) => t.qty > 0)
          .sort((a, b) => a.qty - b.qty);

        this.qty = {};
        this.products.forEach((p) => (this.qty[p.handle] = 0));

        this.els = {
          count: this.querySelector("[data-sprayo-count]"),
          tally: this.querySelector("[data-sprayo-tally]"),
          tallyCount: this.querySelector("[data-sprayo-tally-count]"),
          tallyWord: this.querySelector("[data-sprayo-tally-word]"),
          tallyTier: this.querySelector("[data-sprayo-tally-tier]"),
          was: this.querySelector("[data-sprayo-was]"),
          total: this.querySelector("[data-sprayo-total]"),
          add: this.querySelector("[data-sprayo-add]"),
          note: this.querySelector("[data-sprayo-note]"),
          error: this.querySelector("[data-sprayo-error]"),
          ladder: this.querySelector("[data-sprayo-ladder]"),
          bottles: this.querySelector("[data-sprayo-bottles]"),
        };

        // Beyond this the strip stops being legible and starts being a wall.
        this.maxBottlesShown = 12;

        this.addEventListener("click", (event) => {
          const step = event.target.closest("[data-sprayo-step]");
          if (step) {
            const row = step.closest("[data-sprayo-row]");
            if (row) this.step(row.dataset.sprayoRow, parseInt(step.dataset.sprayoStep, 10));
            return;
          }
          if (event.target.closest("[data-sprayo-add]")) this.submit();
        });

        this.renderLadder();
        this.render();
      }

      /* Shopify's own money_format, so prices match the rest of the site. */
      money(cents) {
        const value = (cents / 100).toFixed(2);
        const [whole, frac] = value.split(".");
        const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return this.moneyFormat
          .replace(/\{\{\s*amount\s*\}\}/g, `${withCommas}.${frac}`)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${frac}`)
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole.replace(/\B(?=(\d{3})+(?!\d))/g, "."));
      }

      total() {
        return Object.values(this.qty).reduce((a, b) => a + b, 0);
      }

      subtotal() {
        return this.products.reduce((sum, p) => sum + p.price * (this.qty[p.handle] || 0), 0);
      }

      /** Highest threshold the current quantity has reached. */
      tierFor(qty) {
        let found = null;
        this.tiers.forEach((t) => {
          if (qty >= t.qty) found = t;
        });
        return found;
      }

      nextTier(qty) {
        return this.tiers.find((t) => qty < t.qty) || null;
      }

      step(handle, delta) {
        const product = this.products.find((p) => p.handle === handle);
        if (!product) return;
        const next = (this.qty[handle] || 0) + delta;
        if (next < 0) return;
        if (next > 0 && !product.available) return;
        this.qty[handle] = next;
        this.render();
      }

      renderLadder() {
        if (!this.els.ladder) return;
        this.els.ladder.innerHTML = this.tiers
          .map(
            (t) =>
              `<li class="sprayo-builder__rung" data-rung="${t.qty}">` +
              `<span class="sprayo-builder__rung-qty">${t.qty}+</span>` +
              `<span class="sprayo-builder__rung-pct">${t.pct}% off</span></li>`
          )
          .join("");
      }

      /** The chosen bottles, drawn. Capped, with the remainder counted. */
      renderBottles() {
        if (!this.els.bottles) return;
        const parts = [];
        let shown = 0;
        this.products.forEach((p) => {
          const n = this.qty[p.handle] || 0;
          for (let i = 0; i < n && shown < this.maxBottlesShown; i += 1, shown += 1) {
            parts.push(
              p.image
                ? `<span class="sprayo-builder__bottle"><img src="${p.image}" alt="" loading="lazy"></span>`
                : `<span class="sprayo-builder__bottle"></span>`
            );
          }
        });
        const remainder = this.total() - shown;
        if (remainder > 0) {
          parts.push(`<span class="sprayo-builder__more-count">+${remainder}</span>`);
        }
        this.els.bottles.innerHTML = parts.join("");
        this.els.bottles.classList.toggle("is-empty", this.total() === 0);
      }

      /** The app's tiers are mix-and-match: it counts bottles across the
          whole order and applies that tier's rate to the lot. So the rung is
          found from the basket's total, not from any one line -- one Black
          and one White is two bottles and earns the two-bottle rate. */
      render() {
        const count = this.total();
        const subtotal = this.subtotal();
        const tier = this.tierFor(count);
        const pct = tier ? tier.pct : 0;
        const total = Math.round((subtotal * (100 - pct)) / 100);

        this.products.forEach((p) => {
          const row = this.querySelector(`[data-sprayo-row="${p.handle}"]`);
          if (!row) return;
          const n = this.qty[p.handle] || 0;
          const value = row.querySelector("[data-sprayo-value]");
          if (value) value.textContent = n;
          const minus = row.querySelector('[data-sprayo-step="-1"]');
          if (minus) minus.setAttribute("aria-disabled", String(n <= 0));
          row.classList.toggle("is-empty", n === 0);
        });

        this.renderBottles();

        this.querySelectorAll("[data-rung]").forEach((rung) => {
          const at = parseInt(rung.dataset.rung, 10);
          rung.classList.toggle("is-reached", count >= at);
          rung.classList.toggle("is-current", tier ? tier.qty === at : false);
        });

        const word = count === 1 ? this.words.one : this.words.many;

        /* The headline number. Kept as its own node rather than folded into
           one string so the figure can be set at display size and the word
           beside it stays body size. */
        if (this.els.tallyCount) this.els.tallyCount.textContent = count;
        if (this.els.tallyWord) this.els.tallyWord.textContent = word;
        if (this.els.tally) this.els.tally.classList.toggle("is-empty", count === 0);
        const saved = subtotal - total;
        if (this.els.tallyTier) {
          /* The amount rather than the percent. On this page the basket can
             run to twenty-four bottles, where "30% off" is a number nobody
             can turn into money in their head. */
          this.els.tallyTier.textContent = "";
          if (saved > 0) {
            this.els.tallyTier.appendChild(document.createTextNode(`${this.saveWord} ${this.money(saved)}`));
            const chip = document.createElement("span");
            chip.className = "sprayo-tier__pct";
            chip.textContent = `${pct}%`;
            this.els.tallyTier.appendChild(chip);
          }
        }

        if (this.els.count) {
          this.els.count.textContent = count
            ? `${count} ${word}${saved > 0 ? ` · ${this.saveWord.toLowerCase()} ${this.money(saved)} · ${pct}% off` : ""}`
            : "";
        }
        if (this.els.total) this.els.total.textContent = count ? this.money(total) : "";
        if (this.els.was) {
          this.els.was.textContent = pct > 0 ? this.money(subtotal) : "";
        }

        const short = Math.max(0, this.min - count);
        if (this.els.add) {
          this.els.add.disabled = short > 0 || count === 0;
        }
        if (this.els.note) {
          const next = this.nextTier(count);
          if (count === 0) {
            this.els.note.textContent = "";
          } else if (short > 0) {
            this.els.note.textContent = `Add ${short} more to build a bundle.`;
          } else if (next) {
            this.els.note.textContent = `Add ${next.qty - count} more for ${next.pct}% off.`;
          } else {
            this.els.note.textContent = "";
          }
        }
      }

      async submit() {
        const items = this.products
          .filter((p) => (this.qty[p.handle] || 0) > 0)
          .map((p) => ({
            id: p.id,
            quantity: this.qty[p.handle],
            properties: {
              Bundle: `${this.total()} ${this.total() === 1 ? this.words.one : this.words.many}`,
              _bundle_id: `sprayo-byo-${Date.now()}`,
            },
          }));
        if (!items.length) return;

        this.setLoading(true);
        this.showError("");
        try {
          const res = await fetch(`${window.routes.cart_add_url}.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/javascript" },
            body: JSON.stringify({
              items,
              sections: "cart-drawer,cart-icon-bubble",
              sections_url: window.location.pathname,
            }),
          });
          const body = await res.json();
          if (!res.ok || body.status) {
            this.showError(
              body.description || body.message || this.dataset.errorGeneric || "Something went wrong."
            );
            return;
          }
          const drawer = document.querySelector("cart-drawer");
          if (drawer && body.sections) {
            const isClosed = !document.body.classList.contains("page-overlay-cart-on");
            drawer.renderContents({ sections: body.sections }, isClosed);
          } else {
            document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
          }
        } catch (e) {
          console.error("[sprayo] builder add to cart failed", e);
          this.showError(this.dataset.errorNetwork || "Could not reach the cart. Please try again.");
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(on) {
        if (!this.els.add) return;
        this.els.add.classList.toggle("loading", on);
        this.els.add.setAttribute("aria-disabled", String(on));
      }

      showError(message) {
        if (!this.els.error) return;
        this.els.error.textContent = message;
        this.els.error.hidden = !message;
      }
    }
  );
}
